import { Component, OnDestroy, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { AvatarPillComponent, ImageUrlPipe, InsightMarkdownPipe } from '@frontend/shared';
import { TranslocoModule, provideTranslocoScope } from '@jsverse/transloco';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { MediaService } from '../../content';
import { getFilterStateByKey } from '../../contributions/contributions-filter-states.constants';
import { DeliveryConversationComponent } from '../../contributions/delivery-conversation/delivery-conversation.component';
import { LoaderGlobeAnimationDetailComponent } from '../../loaders';
import { BreadcrumbService } from '../../navigation/breadcrumb/breadcrumb.service';
import { PageTitleService } from '../../navigation/title/title.service';
import { CommunityInput, CommunityInputError } from '../community-input.model';
import { CommunityInputService } from '../community-input.service';

/**
 * The work screen of a short video for a community input: the question handed out, how to record
 * it, and how to send it — the product owner, 2026-10-13.
 *
 * **The video never passes through the platform.** The member records it on their phone and sends
 * it by WhatsApp to our messenger account, with a code — their delivery's id — so staff can match
 * it to this delivery. The number comes from the server's configuration, served only to the member
 * who has taken the input, and never written into the input's texts. On a phone, *Open WhatsApp*
 * starts the chat with the message written; on a computer, a QR code does the same from the phone.
 * Once sent, *I've sent my video* hands the delivery in, so staff know to look for it.
 *
 * **One delivery per screen.** Staff may give a member several videos from one input, one per
 * subject, so the address carries `?delivery=<id>` to say which; without it, the latest is shown.
 *
 * **Taking it created an insight**, as for insight writing, but the member never writes it: staff
 * attach the approved video to that insight and write its short summary, so this screen links to
 * no editor.
 *
 * Above that: the question and its culture, the input's picture of the four recording styles, the
 * input's own instructions, the task's recording tips, the task's guidance texts as they are added,
 * and the private conversation with staff.
 */
@Component({
  selector: 'multisite-input-video',
  templateUrl: './input-video.component.html',
  styleUrl: './input-video.component.scss',
  imports: [
    RouterModule,
    TranslocoModule,
    InsightMarkdownPipe,
    ImageUrlPipe,
    AvatarPillComponent,
    LoaderGlobeAnimationDetailComponent,
    DeliveryConversationComponent,
  ],
  providers: [provideTranslocoScope('inputs'), provideTranslocoScope('contributions')],
})
export class InputVideoComponent implements OnDestroy {
  private route = inject(ActivatedRoute);
  private inputs = inject(CommunityInputService);
  private pageTitleService = inject(PageTitleService);
  private breadcrumbService = inject(BreadcrumbService);
  private media = inject(MediaService);

  readonly heroTransform = 'w_1200,c_limit';

  input = signal<CommunityInput | null>(null);
  failure = signal<CommunityInputError | null>(null);
  actionFailure = signal<CommunityInputError | null>(null);
  loading = signal(false);
  handingIn = signal(false);
  /** The code was copied: the button says so for a moment. */
  copied = signal(false);

  readonly delivery = computed(() => this.input()?.myDelivery ?? null);
  readonly submission = computed(() => this.input()?.videoSubmission ?? null);
  readonly handedIn = computed(() => !!this.delivery()?.submittedAt);
  readonly myState = computed(() => {
    const state = this.delivery()?.state;
    return state ? getFilterStateByKey(state) ?? null : null;
  });

  private slug = this.route.snapshot.paramMap.get('slug') ?? '';
  /** Which of the member's deliveries from this input, when staff gave them several. */
  private deliveryId = Number(this.route.snapshot.queryParamMap.get('delivery')) || null;
  private subscriptions: Subscription[] = [];
  private copiedTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    this.load();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());

    if (this.copiedTimer) {
      clearTimeout(this.copiedTimer);
    }
  }

  /** A round flag, or the platform's "missing" flag when there is none. */
  flagUrl(hash: string | null): string {
    return this.media.getFlagUrlFromHash(hash ?? '', '', true, '.png');
  }

  copyCode(): void {
    const code = this.submission()?.code;

    if (!code || !navigator.clipboard) {
      return;
    }

    navigator.clipboard.writeText(code).then(() => {
      this.copied.set(true);

      if (this.copiedTimer) {
        clearTimeout(this.copiedTimer);
      }
      this.copiedTimer = setTimeout(() => this.copied.set(false), 2000);
    });
  }

  handIn(): void {
    const input = this.input();
    const delivery = this.delivery();

    if (!input || !delivery || this.handingIn() || this.handedIn()) {
      return;
    }

    this.handingIn.set(true);
    this.actionFailure.set(null);

    this.subscriptions.push(
      this.inputs
        .submitReview(input.slug, delivery.id)
        .pipe(finalize(() => this.handingIn.set(false)))
        .subscribe({
          next: (updated) => this.input.set(updated),
          error: (failure: CommunityInputError) => this.actionFailure.set(failure),
        }),
    );
  }

  private load(): void {
    this.loading.set(true);
    this.failure.set(null);

    this.subscriptions.push(
      this.inputs
        .getOne(this.slug, null, this.deliveryId)
        .pipe(finalize(() => this.loading.set(false)))
        .subscribe({
          next: (input) => {
            this.input.set(input);
            this.pageTitleService.setTitle(input.name ?? '');
            // The crumb for `:slug`, which links back to the input's page, is named after it.
            this.breadcrumbService.setBreadcrumbFragment({ urlFragment: 'slug', fragmentName: input.name ?? '' });
          },
          error: (failure: CommunityInputError) => this.failure.set(failure),
        }),
    );
  }
}
